import { useEffect, useRef, type ReactNode, type ElementType } from 'react';

/**
 * Scroll-reveal motion.
 *
 * Elements marked `[data-reveal]` start hidden (see index.css) and animate in
 * the first time they scroll into view. This hook wires the IntersectionObserver
 * that flips them on.
 *
 * Rules this obeys, because these are data screens and not a landing page:
 *   - Reveal ONCE. Content must not re-animate every time you scroll past it —
 *     an operator scanning a table would be fighting the page.
 *   - Never gate content on JS. If the observer never fires (older browser,
 *     JS error upstream), a failsafe reveals everything after 1.2s, so nothing
 *     is ever permanently invisible.
 *   - Respect prefers-reduced-motion — handled in CSS, which wins outright.
 */
export function useReveal(): void {
  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-in)'));
    if (!nodes.length) return;

    // No observer support → show everything immediately rather than hiding it.
    if (typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('is-in');
          io.unobserve(e.target); // once only
        }
      },
      // Fire slightly before the element is fully on screen so the motion has
      // finished by the time the reader's eye arrives.
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );

    nodes.forEach((n) => io.observe(n));

    // Failsafe: content is never allowed to stay hidden because of motion.
    const failsafe = window.setTimeout(() => {
      nodes.forEach((n) => n.classList.add('is-in'));
    }, 1200);

    return () => {
      io.disconnect();
      window.clearTimeout(failsafe);
    };
  });
}

/**
 * Wrap anything to reveal it on scroll.
 *
 *   <Reveal delay={100}><StatCards … /></Reveal>
 */
export function Reveal({
  children,
  as: Tag = 'div',
  variant,
  delay = 0,
  className,
}: {
  children: ReactNode;
  as?: ElementType;
  /** Direction the element travels in from. Default: up. */
  variant?: 'up' | 'scale' | 'left' | 'right';
  /** Milliseconds to hold before animating — use to cascade siblings. */
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      el?.classList.add('is-in');
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );
    io.observe(el);
    const failsafe = window.setTimeout(() => el.classList.add('is-in'), 1200);
    return () => {
      io.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);

  return (
    <Tag
      ref={ref}
      data-reveal={variant && variant !== 'up' ? variant : ''}
      style={delay ? ({ ['--reveal-delay' as any]: `${delay}ms` } as any) : undefined}
      className={className}
    >
      {children}
    </Tag>
  );
}
